import { useRouter } from 'next/router'
import { ClipLoader } from 'react-spinners'
import { useEffect } from 'react'
import usePostSubmitOrder from '@/src/common/hooks/usePostSubmitOrder'
import {
    PG_CART_SESSION_UUID,
    PG_CART_STATE_UUID,
    RISKIFIED_SESSION_ID,
    WL_CART_SESSION_UUID,
    WL_CART_STATE_UUID,
} from '@/config/cookieNames'
import { useCookies } from 'react-cookie'
import { useCartContext } from '@/src/common/contexts/CartContext'
import {
    addPaymentInfoAnalyticEvent,
    sendBasicEvent,
} from '@/src/common/utilities/gtag'
import { CenterContentContainer } from '@/src/common/components/elements/StyledComponents'
import { PARTNER_DATA_COOKIE, PARTNER_SLUG } from '@/src/common/constants'
import {
    PARTNER_CHECKOUT_PAGE,
    PARTNER_CONFIRMATION_PAGE,
} from '@/src/modules/pg-plus/constants'

export default function AmazonPayRedirect() {
    const router = useRouter()
    const { mutateAsync: submitOrder } = usePostSubmitOrder()

    const payment_intent_id = router.query.payment_intent
    const payment_intent_client_secret =
        router.query.payment_intent_client_secret
    const redirect_status = router.query.redirect_status
    const cart_session_uuid = router.query.cart_session_uuid
    const kount_session_id = router.query.kount_session_id

    const [cookies, setCookie, removeCookie] = useCookies([
        PG_CART_SESSION_UUID,
        PG_CART_STATE_UUID,
        PARTNER_DATA_COOKIE,
        PARTNER_SLUG,
        RISKIFIED_SESSION_ID,
        WL_CART_SESSION_UUID,
        WL_CART_STATE_UUID,
    ])

    const hasPartnerData = Boolean(
        cookies[PARTNER_DATA_COOKIE] || cookies[PARTNER_SLUG]
    )
    const { setCartTotalLoad, setCartTotalCards } = useCartContext()

    const redirectToCheckout = () => {
        void router.push(
            {
                pathname: hasPartnerData ? PARTNER_CHECKOUT_PAGE : '/checkout',
                query: { amazon_failure: 'true' },
            },
            hasPartnerData ? PARTNER_CHECKOUT_PAGE : '/checkout'
        )
    }

    useEffect(() => {
        if (!router.isReady) {
            return
        }

        if (
            redirect_status === 'failed' ||
            !cart_session_uuid ||
            !payment_intent_client_secret ||
            !payment_intent_id
        ) {
            redirectToCheckout()
            return
        }

        submitOrder({
            cart_session_uuid: cart_session_uuid,
            email: '',
            phone: '',
            payment_intent_id: payment_intent_id,
            kount_session_id: kount_session_id,
            riskified_session_id: window.RISKX?.getCartId()
                ? window.RISKX?.getCartId()
                : cookies[RISKIFIED_SESSION_ID] ?? 'unknown',
        })
            .then((data: any) => {
                const { order_number, email, is_guest, phone, amount } = data

                router
                    .push({
                        pathname: hasPartnerData
                            ? PARTNER_CONFIRMATION_PAGE
                            : '/checkout/confirmation',
                        query: {
                            o: order_number,
                            e: email,
                            g: is_guest,
                        },
                    })
                    .then(() => {
                        // Removing the cart cookies effectively clears the cart within the client.
                        removeCookie(
                            hasPartnerData
                                ? WL_CART_SESSION_UUID
                                : PG_CART_SESSION_UUID,
                            {
                                path: '/',
                                domain: window.location.hostname,
                            }
                        )
                        removeCookie(
                            hasPartnerData
                                ? WL_CART_STATE_UUID
                                : PG_CART_STATE_UUID,
                            { path: '/', domain: window.location.hostname }
                        )
                        setCartTotalCards(0)
                        setCartTotalLoad(0)
                        sendBasicEvent(
                            phone ? 'phone_number_added' : 'no_phone_number'
                        )
                        addPaymentInfoAnalyticEvent(amount)
                    })
            })
            .catch(() => {
                redirectToCheckout()
            })
    }, [router.isReady])

    return (
        <CenterContentContainer>
            Processing Order...{' '}
            <ClipLoader
                color={'black'}
                loading={true}
                size={60}
                data-testid={'spinner'}
            />
        </CenterContentContainer>
    )
}

export async function getStaticProps() {
    return {
        props: {
            content: {
                data: {
                    title: 'Amazon Pay Redirect for Gift Cards',
                    description: 'Redirect from Amazon',
                },
            },
        },
    }
}
